/**
 * Cross-section circumference computation from multi-angle silhouette observations.
 *
 * Uses Cauchy's formula from integral geometry:
 *   Perimeter = integral from 0 to pi of w(theta) d(theta)
 *
 * where w(theta) is the projected width at viewing angle theta.
 * This is EXACT for convex shapes (human torso cross-sections are approximately convex).
 */

/** A single silhouette width observation at a given viewing angle */
export interface SilhouetteObservation {
  /** Viewing angle in degrees (0 = front, 90 = right side, 180 = back) */
  angle: number;
  /** Measured silhouette width in cm at this angle */
  widthCm: number;
}

/** Result of ellipse fitting from projected widths */
export interface EllipseFit {
  semiMajor: number;
  semiMinor: number;
  majorAxisAngle: number;
}

/**
 * Compute circumference from silhouette observations using the best available method.
 *
 * - With <2 observations: single-view ellipse estimate (worst accuracy)
 * - With 2-5 observations: fit ellipse from projected widths
 * - With 6+ observations: Cauchy's formula via trapezoidal integration (best accuracy)
 */
export function computeCircumferenceFromObservations(
  observations: SilhouetteObservation[]
): number {
  const valid = observations.filter((o) => o.widthCm > 0);

  if (valid.length === 0) return 0;

  if (valid.length === 1) {
    const a = valid[0]!.widthCm / 2;
    const b = a * 0.38;
    return ellipsePerimeter(a, b);
  }

  if (valid.length < 6) {
    const fit = fitEllipseFromProjections(valid);
    return ellipsePerimeter(fit.semiMajor, fit.semiMinor);
  }

  return cauchyPerimeter(valid);
}

/**
 * Fit an ellipse to the observed projected widths.
 *
 * For an ellipse with semi-axes a, b:
 * - Maximum projected width = 2a (at the angle perpendicular to major axis)
 * - Minimum projected width = 2b (at the angle perpendicular to minor axis)
 */
export function fitEllipseFromProjections(
  observations: SilhouetteObservation[]
): EllipseFit {
  if (observations.length === 0) {
    return { semiMajor: 15, semiMinor: 10, majorAxisAngle: 0 };
  }

  let maxWidth = 0;
  let maxAngle = 0;
  let minWidth = Infinity;

  for (const obs of observations) {
    if (obs.widthCm > maxWidth) {
      maxWidth = obs.widthCm;
      maxAngle = obs.angle;
    }
    if (obs.widthCm < minWidth) {
      minWidth = obs.widthCm;
    }
  }

  if (minWidth === Infinity) minWidth = maxWidth;

  return {
    semiMajor: maxWidth / 2,
    semiMinor: Math.max(minWidth / 2, 1),
    majorAxisAngle: maxAngle,
  };
}

/**
 * Cauchy's formula for perimeter from projected widths.
 *
 * P = integral from 0 to pi of w(theta) d(theta)
 *
 * Since w(theta) == w(theta + 180) for any convex shape,
 * we only need observations over a 180-degree range.
 * We use trapezoidal integration for the discrete approximation.
 */
export function cauchyPerimeter(observations: SilhouetteObservation[]): number {
  // Normalize angles to [0, 360) then fold to [0, 180)
  const folded: SilhouetteObservation[] = observations.map((obs) => {
    const norm = ((obs.angle % 360) + 360) % 360;
    return {
      angle: norm >= 180 ? norm - 180 : norm,
      widthCm: obs.widthCm,
    };
  });

  folded.sort((a, b) => a.angle - b.angle);

  // Merge observations at very close angles (within 5 degrees) by averaging
  const merged: SilhouetteObservation[] = [];
  for (const obs of folded) {
    const last = merged[merged.length - 1];
    if (last && Math.abs(obs.angle - last.angle) < 5) {
      last.widthCm = (last.widthCm + obs.widthCm) / 2;
      last.angle = (last.angle + obs.angle) / 2;
    } else {
      merged.push({ ...obs });
    }
  }

  if (merged.length < 2) {
    const fit = fitEllipseFromProjections(observations);
    return ellipsePerimeter(fit.semiMajor, fit.semiMinor);
  }

  // Trapezoidal integration over [0, 180] degrees
  let integral = 0;

  // Extend first value to 0 degrees
  if (merged[0]!.angle > 0) {
    const dTheta = merged[0]!.angle * (Math.PI / 180);
    integral += merged[0]!.widthCm * dTheta;
  }

  // Trapezoids between consecutive observations
  for (let i = 1; i < merged.length; i++) {
    const dTheta = (merged[i]!.angle - merged[i - 1]!.angle) * (Math.PI / 180);
    const avgWidth = (merged[i]!.widthCm + merged[i - 1]!.widthCm) / 2;
    integral += avgWidth * dTheta;
  }

  // Extend last value to 180 degrees
  const lastAngle = merged[merged.length - 1]!.angle;
  if (lastAngle < 180) {
    const dTheta = (180 - lastAngle) * (Math.PI / 180);
    integral += merged[merged.length - 1]!.widthCm * dTheta;
  }

  return integral;
}

/**
 * Ellipse perimeter using Ramanujan's approximation.
 * P ~ pi * (3(a+b) - sqrt((3a+b)(a+3b)))
 * Accurate to within 0.04% for any ellipse.
 */
export function ellipsePerimeter(a: number, b: number): number {
  if (a <= 0 || b <= 0) return 0;
  const [major, minor] = a >= b ? [a, b] : [b, a];
  return Math.PI * (3 * (major + minor) - Math.sqrt((3 * major + minor) * (major + 3 * minor)));
}
